import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { IMAGES, CONTACT } from "@/data/content";
import { useLanguage } from "@/context/LanguageContext";

const About = () => {
  const { t } = useLanguage();

  return (
    <section id="about" className="relative py-24 md:py-36 bg-[#f8fff8]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-start">
          {/* Left — sticky heading */}
          <div className="lg:sticky lg:top-32">
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-[#d4af37]" />
              <span className="eyebrow">{t("about.eyebrow")}</span>
            </div>
            <Reveal>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#222222] mt-6 leading-[1.03]">
                {t("about.title")}<br />
                <span className="text-[#40916c]">{t("about.titleHighlight")}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-body text-base md:text-lg text-[#5b6b62] leading-relaxed mt-8 max-w-lg">
                {t("about.desc1")}
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="font-body text-base text-[#5b6b62] leading-relaxed mt-5 max-w-lg">
                {t("about.desc2")}
              </p>
            </Reveal>

            <Reveal delay={0.26}>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <span className="px-4 py-2 rounded-full bg-[#d8f3dc] text-[#2d6a4f] font-body text-xs font-semibold">
                  {t("about.tag1")}
                </span>
                <span className="px-4 py-2 rounded-full bg-[#d8f3dc] text-[#2d6a4f] font-body text-xs font-semibold">
                  {t("about.tag2")}
                </span>
                <span className="px-4 py-2 rounded-full bg-[#d8f3dc] text-[#2d6a4f] font-body text-xs font-semibold">
                  {t("about.tag3")}
                </span>
              </div>
            </Reveal>
          </div>

          {/* Right — layered imagery */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative rounded-[28px] overflow-hidden shadow-2xl shadow-[#2d6a4f]/10"
            >
              <img src={IMAGES.paddy} alt="Lush rice paddy" className="w-full h-[520px] object-cover" loading="lazy" />
              <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-[28px]" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="absolute -bottom-10 -left-6 md:-left-12 w-44 md:w-56 rounded-[22px] overflow-hidden shadow-xl ring-4 ring-[#f8fff8]"
            >
              <img src={IMAGES.grainsMacro} alt="Premium rice grains close-up" className="w-full h-40 md:h-48 object-cover" loading="lazy" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="absolute -top-6 -right-4 md:-right-8 bg-white rounded-2xl shadow-xl border border-[#40916c]/10 px-6 py-5 max-w-[220px]"
            >
              <p className="font-display text-4xl font-bold text-[#40916c] leading-none">100%</p>
              <p className="font-body text-xs text-[#5b6b62] mt-1.5 tracking-wide">{t("about.badgeDesc")}</p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
